import { create } from "zustand";
import { IFileDataBase64 } from "../interface/fileDataBase64.interface";
import { AlertStatusType, IAlertState } from "../interface/alert-state.interface";
import { initAlertState } from "../data/initialize/alert-states.data";
import { BootstrapColorType } from "../types/bootstrap.type";

export type AddFileModalStepType = 'form' | 'successMsg';

export interface IAddFileModalData {
    isModalOpen: boolean;
    modalStep: AddFileModalStepType;
    alertState: IAlertState;
    personIdNum: string;
    vehicleNum: string;
    otpCode: string;
    form: IAddFileModalForm;
}

export interface IAddFileModalForm {
    value: IFileDataBase64 | null;
    error: string;
    touched: boolean
    isValid: boolean;
}

interface IAddFileModalStore {
    fileModalData: IAddFileModalData;
    setFileValue: (value: any) => void;
    setFileError: (isValid: boolean, errorMsg?: string) => void;
    resetAddFileModalData: () => void;
    openAddFileModal: (personIdNum: string, vehicleNum: string, otpCode: string) => void;
    closeAddFileModal: () => void;
    setAlertState: (status: AlertStatusType, msg?: string, color?: BootstrapColorType | '') => void;
    setStepModalAsSuccessMsg: () => void;
}


const initAddFileModalData: IAddFileModalData = {
    isModalOpen: false,
    modalStep: 'form',
    alertState: initAlertState,
    personIdNum: '',
    vehicleNum: '',
    otpCode: '',
    form: {
        value: null,
        error: '',
        touched: false,
        isValid: false,
    },
};

export const useAddFileModalStore = create<IAddFileModalStore>()((set) => ({
    fileModalData: initAddFileModalData,
    setFileValue: (value: IFileDataBase64) => set((state) => ({
        ...state,
        fileModalData: {
            ...state.fileModalData,
            form: {
                ...state.fileModalData.form,
                value: value,
            }
        }
    })),
    setFileError: (isValid: boolean, errorMsg: string = '') => set((state) => ({
        ...state,
        fileModalData: {
            ...state.fileModalData,
            form: {
                ...state.fileModalData.form,
                isValid: isValid,
                error: errorMsg,
                touched: true,
            }
        }
    })),
    resetAddFileModalData: () => set((state) => ({
        ...state,
        fileModalData: initAddFileModalData
    })),
    openAddFileModal: (personIdNum: string, vehicleNum: string, otpCode: string) => set((state) => ({
        ...state,
        fileModalData: {
            ...state.fileModalData,
            isModalOpen: true,
            modalStep: 'form',
            personIdNum,
            vehicleNum,
            otpCode,
        }
    })),
    closeAddFileModal: () => set((state) => ({
        ...state,
        fileModalData: {
            ...state.fileModalData,
            isModalOpen: false,
            modalStep: 'form',
        }
    })),
    setAlertState: (status: AlertStatusType, msg?: string, color?: BootstrapColorType | '') => set((state) => ({
        ...state,
        fileModalData: {
            ...state.fileModalData,
            alertState: {
                alertStatus: status,
                alertColor: color || '',
                message: msg || '',
            }
        }
    })),
    setStepModalAsSuccessMsg: () => set((state) => ({
        ...state,
        fileModalData: {
            ...state.fileModalData,
            modalStep: 'successMsg',
        }
    })),
}));


export const useGetPersonIdNumAddFileModal = () => useAddFileModalStore((state) => state.fileModalData.personIdNum);
export const useGetVehicleNumAddFileModal = () => useAddFileModalStore((state) => state.fileModalData.vehicleNum);
export const useGetOtpCodeAddFileModal = () => useAddFileModalStore((state) => state.fileModalData.otpCode);
export const useIsAddFileModalOpen = () => useAddFileModalStore((state) => state.fileModalData.isModalOpen);
export const useGetAddFileModalForm = () => useAddFileModalStore((state) => state.fileModalData.form);
export const useGetAlertStateAddFileModal = () => useAddFileModalStore((state) => state.fileModalData.alertState);
export const useGetModalStepAddFileModal = () => useAddFileModalStore((state) => state.fileModalData.modalStep);
export const useSetStepModalAddFileAsSuccessMsg = () => useAddFileModalStore((state) => state.setStepModalAsSuccessMsg);
export const useOpenAddFileModal = () => useAddFileModalStore((state) => state.openAddFileModal);
export const useCloseAddFileModal = () => useAddFileModalStore((state) => state.closeAddFileModal);
export const useSetFileValue = () => useAddFileModalStore((state) => state.setFileValue);
export const useSetFileError = () => useAddFileModalStore((state) => state.setFileError);
export const useResetAddFileModalData = () => useAddFileModalStore((state) => state.resetAddFileModalData);
export const useSetAlertStateAddFileModal = () => useAddFileModalStore((state) => state.setAlertState);